import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { facilityApi } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { 
  HomeIcon, 
  CalendarIcon, 
  ArrowRightOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import StaffCourtReservations from './StaffCourtReservations';
import StaffEvents from './StaffEvents';
import StaffClasses from './StaffClasses';

const StaffDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [facilityName, setFacilityName] = useState<string>('');

  useEffect(() => {
    const fetchFacilityData = async () => {
      try {
        setLoading(true);
        
        if (!authUser || !authUser.businessId || !authUser.facilityId) {
          setError('Missing required user data');
          return;
        }

        // Fetch facility name if we have a facility ID
        const facility = await facilityApi.getFacility(authUser.facilityId, authUser.businessId);
        setFacilityName(facility.name);
      } catch (err) {
        setError('Failed to fetch facility data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFacilityData();
  }, [authUser]);

  const handleLogout = async () => {
    await logout();
    navigate('/staff/login');
  };

  const getActiveSection = () => {
    const path = location.pathname;
    if (path.includes('/schedule')) return 'schedule';
    if (path.includes('/events')) return 'events';
    if (path.includes('/classes')) return 'classes';
    return 'overview';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-red-500">{error}</div>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Staff user not found</div>
      </div>
    );
  }

  const navigationItems = [
    { 
      name: 'Dashboard', 
      href: '/staff/dashboard', 
      icon: HomeIcon,
      section: 'overview'
    },
    { 
      name: 'Court Schedule', 
      href: '/staff/dashboard/schedule', 
      icon: CalendarIcon,
      section: 'schedule'
    },
    {
      name: 'Events',
      href: '/staff/dashboard/events',
      icon: CalendarIcon,
      section: 'events'
    },
    {
      name: 'Classes',
      href: '/staff/dashboard/classes',
      icon: CalendarIcon,
      section: 'classes'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex">
        {/* Sidebar Navigation */}
        <div className={`${isSidebarCollapsed ? 'w-16' : 'w-64'} bg-white shadow-sm h-screen fixed transition-all duration-300`}>
          <div className="flex flex-col h-full">
            <div className="flex-shrink-0 px-4 py-4 border-b">
              <div className="flex items-center justify-between">
                {!isSidebarCollapsed && (
                  <h1 className="text-xl font-bold text-gray-900">Staff Portal</h1>
                )}
                <button
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="p-2 rounded-md hover:bg-gray-100"
                >
                  {isSidebarCollapsed ? (
                    <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
              </div>
              {!isSidebarCollapsed && authUser.facilityId && (
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                  {facilityName || 'Loading facility...'}
                </div>
              )}
            </div>
            <nav className="flex-1 px-2 py-4 space-y-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      getActiveSection() === item.section
                        ? 'bg-indigo-50 text-indigo-600 border-indigo-500'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-transparent'
                    } group flex items-center px-3 py-2 text-sm font-medium rounded-md border-l-2`}
                  >
                    <Icon className={`${isSidebarCollapsed ? 'mx-auto' : 'mr-3'} h-5 w-5 flex-shrink-0`} />
                    {!isSidebarCollapsed && item.name}
                  </Link>
                );
              })}
            </nav>
            <div className="flex-shrink-0 px-4 py-4 border-t">
              <button 
                onClick={handleLogout}
                className={`w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 ${
                  isSidebarCollapsed ? 'px-2' : ''
                }`}
              >
                {isSidebarCollapsed ? (
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                ) : (
                  'Logout'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
          <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <div className="px-4 py-6 sm:px-0">
              <div className="mt-6">
                {getActiveSection() === 'overview' && (
                  <div className="bg-white shadow rounded-lg p-6">
                    <h2 className="text-lg font-medium text-gray-900">Staff Overview</h2>
                    <div className="mt-4">
                      <p><strong>Name:</strong> {authUser.name}</p>
                      <p><strong>Email:</strong> {authUser.email}</p>
                      <p><strong>Phone:</strong> {authUser.phone}</p>
                      <p><strong>Role:</strong> {authUser.role}</p>
                      <p><strong>Facility ID:</strong> {authUser.facilityId}</p>
                    </div>
                  </div>
                )}

                {getActiveSection() === 'schedule' && (
                  <div className="px-4 py-6 sm:px-0">
                    <StaffCourtReservations />
                  </div>
                )}

                {getActiveSection() === 'events' && (
                  <div className="px-4 py-6 sm:px-0">
                    <StaffEvents />
                  </div>
                )}

                {getActiveSection() === 'classes' && (
                  <div className="px-4 py-6 sm:px-0">
                    <StaffClasses />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard; 